import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { ReactComponent as BoltIcon } from "assets/images/icons/Bolt.svg";
import { ReactComponent as CalculatorIcon } from "assets/images/icons/Calculator.svg";
import { ReactComponent as ChartIcon } from "assets/images/icons/Chart.svg";
import { ReactComponent as FileInvoiceDollarIcon } from "assets/images/icons/FileInvoiceDollar.svg";
import { ReactComponent as HomeIcon } from "assets/images/icons/Home.svg";
import { ReactComponent as ShipIcon } from "assets/images/icons/Ship.svg";
import { ReactComponent as TasksIcon } from "assets/images/icons/Tasks.svg";
import usePermissions from "hooks/usePermissions";
import useToken from "hooks/useToken";
import testIds from "constants/testIds";
import { IOtherPermissionProps, IPermissionProps, IItemProps } from "components/molecules/Navigation/interface/sidebar";
import { useGetPendingTasks } from "api/queries/user/useGetPendingTasks";
import MenuItem from "../common/MenuItem";
import NewRequest from "../common/NewRequest";

interface IShipperMenuItemsProps {
  handleCloseNav: () => void;
}

const ShipperMenuItems = function ({ handleCloseNav }: IShipperMenuItemsProps) {
  const intl = useIntl();
  const { getPermissions, clearPermissions } = usePermissions();
  const { token } = useToken();
  const [userPermissions, setUserPermissions] = useState<IPermissionProps>({});
  const [otherPermissions, setOtherPermissions] = useState<IOtherPermissionProps>({});
  const permissions = getPermissions();
  const { data: pendingTasksData } = useGetPendingTasks({ options: { refetchInterval: 300000 } });

  useEffect(() => {
    if (token && permissions) {
      setUserPermissions(JSON.parse(permissions).menu_items);
      setOtherPermissions(JSON.parse(permissions).permissions);
    } else clearPermissions();
  }, [token, permissions]);

  const totalPendingTasks =
    (pendingTasksData?.approval_requests || 0) +
    (pendingTasksData?.post_shipment_survey || 0) +
    (pendingTasksData?.request_to_cancel_quote_request || 0);

  const navigationTop: IItemProps[] = [
    {
      eventKey: "1",
      id: "dashboard",
      icon: <HomeIcon />,
      label: intl.formatMessage({ id: "components.molecules.navigation.dashboard", defaultMessage: "Dashboard" }),
      parentUrl: "/",
      to: "/",
      enabled: true,
      testId: testIds.MENU_DASHBOARD_BUTTON,
    },
    {
      eventKey: "2",
      id: "view_quick_requests",
      icon: <BoltIcon />,
      label: intl.formatMessage({ id: "components.molecules.navigation.quickRequests", defaultMessage: "Quick Requests" }),
      parentUrl: "/quick-requests",
      to: "/quick-requests?display_status=pending",
      enabled: userPermissions.view_quick_requests,
      testId: testIds.MENU_QUICK_REQUESTS_BUTTON,
    },
    {
      eventKey: "3",
      id: "view_quote_requests",
      icon: <CalculatorIcon />,
      label: intl.formatMessage({
        id: "components.molecules.navigation.quoteRequests",
        defaultMessage: "Quote Requests",
      }),
      parentUrl: "/quote-requests",
      to: "/quote-requests?display_status=awaiting_action",
      enabled: userPermissions.view_quote_requests,
      testId: testIds.MENU_QUOTE_REQUESTS_BUTTON,
    },
    {
      eventKey: "5",
      id: "shipments",
      icon: <ShipIcon />,
      label: intl.formatMessage({
        id: "general.shipments",
        defaultMessage: "Shipments",
      }),
      parentUrl: "/shipments",
      to: "/shipments?display_status=In transit",
      enabled: userPermissions.view_shipments,
      testId: testIds.MENU_SHIPMENTS_BUTTON,
    },
    {
      eventKey: "6",
      id: "view_invoices",
      icon: <FileInvoiceDollarIcon />,
      label: intl.formatMessage({
        id: "general.invoices",
        defaultMessage: "Invoices",
      }),
      parentUrl: "/invoices",
      to: "/invoices?display_status=awaiting_audit",
      enabled: userPermissions.view_invoices,
      testId: testIds.MENU_INVOICES_BUTTON,
    },
    {
      eventKey: "7",
      id: "view_analytics",
      icon: <ChartIcon />,
      label: intl.formatMessage({
        id: "components.molecules.navigation.analytics",
        defaultMessage: "Analytics",
      }),
      parentUrl: "/analytics",
      to: "/analytics?filter=reports",
      enabled: userPermissions.view_analytics,
      testId: testIds.MENU_ANALYTICS_BUTTON,
    },
    {
      eventKey: "8",
      id: "resolution_center",
      icon: <TasksIcon />,
      label: intl.formatMessage({
        id: "general.tasks",
        defaultMessage: "Tasks",
      }),
      unreadCount: totalPendingTasks,
      shouldBlink: pendingTasksData?.approval_requests > 0,

      parentUrl: "/tasks",
      to: "/tasks?status=open",
      enabled: userPermissions.view_analytics,
      testId: testIds.MENU_TASKS_BUTTON,
    },
  ];

  return (
    <div className="menu-item-center w-100">
      {navigationTop.map((item) => (
        <MenuItem item={item} key={item.id} closeNav={handleCloseNav} testId={item.testId} />
      ))}
      <NewRequest permissions={userPermissions} otherPermissions={otherPermissions} />
    </div>
  );
};

export default ShipperMenuItems;
